/**
 * @file src/sync/drift.js
 * @description Pure drift math for the shared timecode: fold a raw (actual − expected) difference
 * into the one honest answer a wrapping playhead can give, and decide which rung of the correction
 * ladder that answer deserves. No state, no clocks, no engine — the audio adapter owns the sampling,
 * the timers and the rate; this file only answers "how far off, and how gently do we fix it".
 *
 * Two positions on a looping (or wrapping) track are only comparable modulo the wrap period: a
 * playhead 10 ms from the end of a 4 s loop and one 10 ms past its start are 20 ms apart, not
 * 3980 ms. Folding to ±period/2 is what makes "behind" and "ahead" meaningful at all — and it is
 * also why a very short loop cannot support the hard-realign rung (see `decideDriftAction`).
 */

/** Below this, the two clocks read as one performance — leave the audio alone. */
export const DRIFT_IGNORE_MS = 25;
/** Above this, a rate trim would take too long to absorb the error — re-align on a bar instead. */
export const DRIFT_REALIGN_MS = 120;
/** The fixed rate trim, signed by the drift direction. 0.5% is inaudible on any material. */
export const DRIFT_TRIM_RATIO = 0.005;
/** Hysteresis: hold the trim until the error is comfortably gone, or it oscillates around the gate. */
export const DRIFT_TRIM_CLEAR_MS = 10;
/** Minimum wall time between two hard re-aligns of one voice. */
export const DRIFT_REALIGN_COOLDOWN_MS = 4_000;
/** Consecutive over-threshold medians before a hard re-align is armed. */
export const DRIFT_REALIGN_CONSECUTIVE = 2;
/** How many samples the median is taken over. */
export const DRIFT_SAMPLE_WINDOW = 3;
/** Sampling period. Hidden tabs throttle this toward ~1 s; audibly-playing tabs are exempt. */
export const DRIFT_SAMPLE_INTERVAL_MS = 500;
/** The PARKED period, used while a voice is eligible but has no joined clock to measure against. */
export const DRIFT_SAMPLE_PARKED_INTERVAL_MS = 5_000;

/**
 * Fold a raw difference into ±period/2 — the only comparison that means anything on a playhead that
 * wraps. Returns null when there is no usable period (unresolved duration, no engaged loop): with
 * nothing to fold against, a threshold decision would be made on an unbounded number.
 * @param {number} deltaMs actual − expected, in ms.
 * @param {number} periodMs the wrap period (engaged loop length, else track duration).
 * @returns {number|null} the folded drift in (−period/2, +period/2], negative = behind.
 */
export function foldDriftMs(deltaMs, periodMs) {
  const delta = Number(deltaMs);
  const period = Number(periodMs);
  if (!Number.isFinite(delta) || !Number.isFinite(period) || period <= 0) return null;
  const wrapped = ((delta % period) + period) % period;
  return wrapped > period / 2 ? wrapped - period : wrapped;
}

/**
 * Median of a small sample window. The median (not the mean) is what makes one glitched read — a
 * buffer stall, a loop-boundary straddle — unable to move a decision on its own.
 * @param {number[]} samples
 * @returns {number|null} null when there is nothing finite to take a median of.
 */
export function medianOf(samples) {
  const sorted = (Array.isArray(samples) ? samples : [])
    .map(Number)
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (sorted.length === 0) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Pick the gentlest rung that will absorb this drift.
 *
 *  - under the clear gate → drop any trim (converged);
 *  - under the ignore gate → leave everything as it is (inside tolerance, trim still working);
 *  - up to the re-align gate → a fixed signed rate trim, negative drift (behind) speeding up;
 *  - beyond it → a hard re-align, unless the fold period is too short to distinguish "far behind"
 *    from "nearly a lap ahead", in which case only the trim is honest.
 *
 * @param {{ medianMs: number, periodMs?: number|null, trim?: number }} input
 * @returns {{ rung: 'none'|'trim'|'realign', trim: number }} `trim` is the value the caller should
 *   hold from now on (unchanged when the rung doesn't touch it).
 */
export function decideDriftAction({ medianMs, periodMs = null, trim = 0 } = {}) {
  const held = Number.isFinite(trim) ? trim : 0;
  // `Number(null)` is 0 — a missing median must read as "nothing to judge", never as "converged".
  if (medianMs == null) return { rung: 'none', trim: held };
  const median = Number(medianMs);
  if (!Number.isFinite(median)) return { rung: 'none', trim: held };
  const magnitude = Math.abs(median);
  if (magnitude < DRIFT_TRIM_CLEAR_MS) return { rung: 'none', trim: 0 };
  if (magnitude < DRIFT_IGNORE_MS) return { rung: 'none', trim: held };
  const period = Number(periodMs);
  const canRealign = !Number.isFinite(period) || period <= 0 || period / 2 >= DRIFT_REALIGN_MS;
  if (magnitude > DRIFT_REALIGN_MS && canRealign) return { rung: 'realign', trim: held };
  return { rung: 'trim', trim: median < 0 ? DRIFT_TRIM_RATIO : -DRIFT_TRIM_RATIO };
}
